import {
  FluidExpandedPosition,
  FluidRowData,
  FluidTableGroupingConfig,
  FluidTableHeader,
  FluidTheme,
  VISIBLE,
} from '@lmig/fluid-core';
import { h } from '@stencil/core';

import { RenderValue } from '../../../common/fluid-component-rendering.components';
import { Utils } from '../../../utils/utils';
import { FluidTable } from '../fluid-table';
import { getComputedDataItems } from '../internal/fluid-table-data-management.utils';
import { flattenHeaders } from '../internal/fluid-table-header.utils';
import { sortRowsBasedOnExpandButtonPosition } from '../internal/fluid-table-sort.utils';
import { ExpandPanelCell } from './fluid-table-cell-types.components';

export const GroupedItems = (props: {
  expanded: boolean;
  dataItems: FluidRowData[];
  render: (dataItem) => any;
}) => {
  return props.expanded
    ? props.dataItems.map((dataItem) => props.render(dataItem))
    : '';
};

export type GroupHeaderProps = {
  headers: FluidTableHeader[];
  dataItems: FluidRowData[];
  visibleDataItems: FluidRowData[];
  selectable: boolean;
  expanded: boolean;
  groupId: string;
  toggleExpand: Function;
  config: FluidTableGroupingConfig;
  allowTabIndexRows: boolean;
  allowTabIndexCells: boolean;
  theme: FluidTheme;
  host: Partial<FluidTable>;
  externalState?: Record<string, unknown>;
};

export const GroupHeader = (props: GroupHeaderProps) => {
  const {
    headers,
    dataItems,
    visibleDataItems,
    selectable,
    expanded,
    groupId,
    toggleExpand,
    allowTabIndexRows,
    allowTabIndexCells,
    theme,
    host,
    externalState,
  } = props;
  const hiddenByEmpty =
    headers.some((h) => h.grouping?.hideEmptyGroups) &&
    !dataItems?.some((dataItem) => dataItem[VISIBLE]);
  const shouldRender =
    headers.some((h) => h.grouping?.headerValue) &&
    !headers.some((h) => h.grouping?.additionalHeaderRow) &&
    visibleDataItems.length !== 0 &&
    !hiddenByEmpty;

  return (
    shouldRender && (
      <tr
        class={'fluid-table-group-header'}
        tabIndex={Utils.setTabIndex(allowTabIndexRows)}>
        <ExpandPanelCell
          noHighlight
          expanded={expanded}
          onClick={() => {
            toggleExpand(groupId);
          }}
          conditionalOn={true}
          placeholderCell={false}
          theme={theme}
        />
        {selectable && <td class='table-data-cell' />}
        {headers.map((header) => {
          const { grouping, cellAlignment } = header;
          const { headerValue } = grouping ?? {};

          const alignment = headerValue
            ? grouping?.cellAlignment
            : cellAlignment;

          const tdClass = {
            'table-data-cell': true,
            sortable: !!headerValue,
            [alignment]: !!alignment,
          };

          return (
            header.visible && (
              <td
                class={tdClass}
                tabIndex={Utils.setTabIndex(allowTabIndexCells)}>
                {headerValue ? (
                  <RenderValue
                    value={headerValue(
                      getComputedDataItems(
                        host,
                        dataItems,
                        header.grouping.useLive
                      ),
                      externalState
                    )}
                    renderComponent={header.grouping.headerRenderComponent}
                    sorted={header.grouping.sorted}
                    sortDirection={header.grouping.sortDirection}
                  />
                ) : null}
              </td>
            )
          );
        })}
        <td class='table-data-cell action-cell' />
      </tr>
    )
  );
};
export type GroupFooterProps = {
  headers: FluidTableHeader[];
  dataItems: FluidRowData[];
  selectable: boolean;
  includeRowActions?: boolean;
  allowTabIndexRows: boolean;
  expandedButtonPosition: FluidExpandedPosition;
  hasExpandedElements: boolean;
  host: Partial<FluidTable>;
  externalState?: Record<string, unknown>;
};
export const GroupFooter = (props: GroupFooterProps) => {
  const {
    headers,
    dataItems,
    selectable,
    includeRowActions,
    allowTabIndexRows,
    expandedButtonPosition,
    hasExpandedElements,
    host,
    externalState,
  } = props;

  const shouldRender = headers.some((h) => !!h.grouping?.footerValue);

  const emptyCell: FluidTableHeader = {
    label: '',
    visible: true,
  };

  // Add an empty cell for expandedButtonPosition, the default for the expanded button is to the left
  const sortedHeaders = hasExpandedElements
    ? sortRowsBasedOnExpandButtonPosition(
        expandedButtonPosition,
        emptyCell,
        headers,
        headers
      )
    : headers;

  return shouldRender ? (
    <tr
      class={'fluid-table-group-footer'}
      tabIndex={Utils.setTabIndex(allowTabIndexRows)}>
      {selectable && <td class='table-data-cell' />}
      {flattenHeaders(sortedHeaders).map((header) => {
        const footerValue = header.grouping?.footerValue;
        return (
          header.visible && (
            <td>
              {footerValue ? (
                <RenderValue
                  value={footerValue(
                    getComputedDataItems(
                      host,
                      dataItems,
                      header.grouping.useLive
                    ),
                    externalState
                  )}
                  renderComponent={header.grouping.footerRenderComponent}
                />
              ) : null}
            </td>
          )
        );
      })}
      {includeRowActions && <td class='table-data-cell action-cell' />}
    </tr>
  ) : null;
};
